/**
 * The Explorer's chart model (D529; doc 20 §4.3, doc 05 §5): which chart types are VALID for the shape of an answer
 * (n measures × up to two cuts), and how the answer's rows become the series, matrices and shares those charts draw. Pure: no
 * React, no clock, no I/O; words and figures come in as callbacks, so the same model serves every language and market.
 *
 * Rules that hold for every chart:
 *  - Money is never summed or drawn on one axis across currencies: the rows are split by currency and each group is its own chart.
 *  - A measure that is not additive (a ratio) is never stacked or shown as a share of a total.
 *  - A figure that is missing stays missing (a gap, an empty cell), never a zero.
 *  - Only the first `MAX_SERIES` series (by total) and `MAX_CATEGORIES` categories are drawn; the table has the rest.
 */
import type { ResultRow } from '@vello/analytics-semantic';
import type { BiUnit } from './bi-core.js';

export type ExplorerChartKind = 'line' | 'columns' | 'bars' | 'donut' | 'pareto' | 'grouped' | 'stacked-columns' | 'stacked-bars' | 'stacked-area' | 'heatmap' | 'combo';

export interface ChartShape {
  readonly metrics: readonly { readonly id: string; readonly unit: string }[];
  readonly dimensions: readonly { readonly id: string }[];
  readonly rowCount: number;
}

/** The cuts that are an ordered axis of time (a line or an area follows them). `weekday` and `hour` of the day are categories. */
export const TIME_AXIS_DIMENSIONS: readonly string[] = ['year', 'quarter', 'month', 'day', 'hour'];

export const MAX_SERIES = 6;
export const MAX_CATEGORIES = 40;

export const isTimeAxis = (id: string): boolean => TIME_AXIS_DIMENSIONS.includes(id);

/** Whether parts of this unit add up to a whole (stacking, shares, a Pareto make sense). A ratio does not. */
export const isAdditive = (unit: string): boolean => unit !== 'ratio';

const sameUnit = (metrics: ChartShape['metrics']): boolean => metrics.every((m) => m.unit === metrics[0]?.unit);

/** A combo needs a first measure for the bars and the rest, on one shared second scale. */
export function comboValid(metrics: ChartShape['metrics']): boolean {
  if (metrics.length < 2) return false;
  const rest = metrics.slice(1);
  return rest.every((m) => m.unit === rest[0]?.unit);
}

/** The chart types that make sense for a result shape, best first. Empty: the table alone says it all (a single total, or three or more cuts). */
export function chartKinds(shape: ChartShape): ExplorerChartKind[] {
  const m = shape.metrics.length;
  const d = shape.dimensions.length;
  if (shape.rowCount === 0 || m === 0 || d === 0 || d > 2) return [];
  const first = shape.metrics[0] as { id: string; unit: string };
  const additive = isAdditive(first.unit);
  const kinds: ExplorerChartKind[] = [];
  if (d === 1) {
    const time = isTimeAxis(shape.dimensions[0]?.id ?? '');
    if (m === 1) {
      if (time) kinds.push('line', 'columns');
      else {
        kinds.push('bars', 'columns');
        if (additive) kinds.push('pareto');
        if (first.unit === 'count') kinds.push('donut');
      }
    } else {
      const same = sameUnit(shape.metrics);
      if (time && same) kinds.push('line');
      if (same) kinds.push('grouped');
      if (comboValid(shape.metrics)) kinds.push('combo');
      if (same && additive) kinds.push(time ? 'stacked-area' : 'stacked-bars');
    }
    return kinds;
  }
  // Two cuts: one measure (the first, or the one the screen picks) over a matrix.
  const anyTime = shape.dimensions.some((x) => isTimeAxis(x.id));
  kinds.push('heatmap');
  if (additive) kinds.push('stacked-columns', 'stacked-bars');
  kinds.push('grouped');
  if (additive && anyTime) kinds.push('stacked-area');
  return kinds;
}

/** The kind to open with: the first valid one, or `undefined` when none is. */
export function defaultChartKind(shape: ChartShape): ExplorerChartKind | undefined {
  return chartKinds(shape)[0];
}

/** Whether a kind takes the 100 % option. */
export function supportsPercent(kind: ExplorerChartKind): boolean {
  return kind === 'stacked-columns' || kind === 'stacked-bars' || kind === 'stacked-area';
}

/** Whether the chart plots ONE measure (so a measure picker is needed when the answer has more). */
export function singleMeasure(kind: ExplorerChartKind, dimensions: number): boolean {
  return dimensions === 2 || kind === 'bars' || kind === 'columns' || kind === 'pareto' || kind === 'donut';
}

// ── Rows to plots ─────────────────────────────────────────────────────────────────────────────────────────────────

export interface CurrencyGroup {
  readonly currency: string | undefined;
  readonly rows: readonly ResultRow[];
}

/** Split rows by currency (rows without one first, then A → Z): money of two currencies never shares an axis. */
export function currencyGroups(rows: readonly ResultRow[]): CurrencyGroup[] {
  const groups = new Map<string, ResultRow[]>();
  for (const r of rows) {
    const k = r.currency ?? '';
    groups.set(k, [...(groups.get(k) ?? []), r]);
  }
  return [...groups.entries()].sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0)).map(([k, rs]) => ({ currency: k === '' ? undefined : k, rows: rs }));
}

export interface Category {
  /** Stable id of the category (the dimension key, or the market-qualified key when markets repeat a key). */
  readonly key: string;
  readonly label: string;
}

/** The text of one cut of a row. */
export type LabelOf = (dimensionId: string, key: string | null | undefined, label: string | null | undefined) => string;

const keyOf = (r: ResultRow, dim: string): string => r.keys[dim]?.key ?? '';

/** Order two keys: numerically when both are numbers (weekday 1…7, hour 0…23), else as text (ISO days and months sort as text). */
function compareKeys(a: string, b: string): number {
  const na = Number(a);
  const nb = Number(b);
  if (a !== '' && b !== '' && Number.isFinite(na) && Number.isFinite(nb)) return na - nb;
  return a < b ? -1 : a > b ? 1 : 0;
}

/** The orderable cuts: time and the weekday / hour of the day read in order; any other cut keeps the order the server gave (its sort). */
export const isOrdered = (dim: string): boolean => isTimeAxis(dim) || dim === 'weekday';

/** The distinct categories of a cut over some rows, in reading order. */
export function categoriesOf(rows: readonly ResultRow[], dim: string, label: LabelOf): Category[] {
  const seen = new Map<string, Category>();
  for (const r of rows) {
    const key = keyOf(r, dim);
    if (!seen.has(key)) seen.set(key, { key, label: label(dim, r.keys[dim]?.key, r.keys[dim]?.label) });
  }
  const list = [...seen.values()];
  return isOrdered(dim) ? list.sort((a, b) => compareKeys(a.key, b.key)) : list;
}

/** A plotted number: `null` for a missing cell. */
export type PlotValue = (metricId: string, row: ResultRow) => number | null;

export interface SeriesModel {
  readonly key: string;
  readonly label: string;
  readonly values: readonly (number | null)[];
}

/** One series per MEASURE over the categories of one cut. */
export function seriesByMetric(rows: readonly ResultRow[], dim: string, categories: readonly Category[], metrics: readonly { id: string; name: string }[], value: PlotValue): SeriesModel[] {
  const at = new Map<string, ResultRow>();
  for (const r of rows) if (!at.has(keyOf(r, dim))) at.set(keyOf(r, dim), r);
  return metrics.map((m) => ({ key: m.id, label: m.name, values: categories.map((c) => (at.has(c.key) ? value(m.id, at.get(c.key) as ResultRow) : null)) }));
}

export interface MatrixModel {
  readonly rows: readonly Category[];
  readonly cols: readonly Category[];
  /** `cells[row][col]`; `null` where the answer has no row. */
  readonly cells: readonly (readonly (number | null)[])[];
  /** How many categories were cut off to keep the chart readable (rows, columns). */
  readonly truncated: { readonly rows: number; readonly cols: number };
}

/**
 * Which cut is the row and which the column: a weekday is a row and an hour of the day a column (the classic hour × weekday
 * grid); a time axis is the column; otherwise the first cut is the row.
 */
export function orientation(dims: readonly [string, string]): readonly [string, string] {
  const [a, b] = dims;
  if (b === 'weekday' || (isTimeAxis(a) && !isTimeAxis(b))) return [b, a];
  return [a, b];
}

/** One measure over two cuts as a matrix, the categories ordered and capped (the biggest rows and columns by total are kept for unordered cuts). */
export function matrixOf(rows: readonly ResultRow[], dims: readonly [string, string], metricId: string, label: LabelOf, value: PlotValue): MatrixModel {
  const [rowDim, colDim] = orientation(dims);
  const allRows = categoriesOf(rows, rowDim, label);
  const allCols = categoriesOf(rows, colDim, label);
  const total = (dim: string, key: string): number => rows.reduce((s, r) => (keyOf(r, dim) === key ? s + (value(metricId, r) ?? 0) : s), 0);
  const keep = (list: Category[], dim: string, cap: number): Category[] => (list.length <= cap ? list : isOrdered(dim) ? list.slice(0, cap) : [...list].sort((a, b) => total(dim, b.key) - total(dim, a.key)).slice(0, cap));
  const rowCats = keep(allRows, rowDim, MAX_CATEGORIES);
  const colCats = keep(allCols, colDim, MAX_CATEGORIES);
  const cell = new Map<string, number | null>();
  for (const r of rows) cell.set(`${keyOf(r, rowDim)}\u0000${keyOf(r, colDim)}`, value(metricId, r));
  return {
    rows: rowCats,
    cols: colCats,
    cells: rowCats.map((rc) => colCats.map((cc) => cell.get(`${rc.key}\u0000${cc.key}`) ?? null)),
    truncated: { rows: allRows.length - rowCats.length, cols: allCols.length - colCats.length },
  };
}

export interface StackModel {
  readonly categories: readonly Category[];
  readonly series: readonly SeriesModel[];
  readonly truncated: { readonly categories: number; readonly series: number };
  /** The orientation used: categories are the first axis, series the second. */
  readonly categoryDimension: string;
  readonly seriesDimension: string;
}

/**
 * One measure over two cuts as series: the ordered cut (a time axis, a weekday) is the CATEGORY axis; else the first cut is.
 * The second cut's biggest `MAX_SERIES` values by total become the series.
 */
export function stackOf(rows: readonly ResultRow[], dims: readonly [string, string], metricId: string, label: LabelOf, value: PlotValue): StackModel {
  const [a, b] = dims;
  const [categoryDimension, seriesDimension] = isOrdered(b) && !isOrdered(a) ? [b, a] : [a, b];
  const allCategories = categoriesOf(rows, categoryDimension, label);
  const categories = allCategories.length > MAX_CATEGORIES ? allCategories.slice(0, MAX_CATEGORIES) : allCategories;
  const allSeries = categoriesOf(rows, seriesDimension, label);
  const total = (key: string): number => rows.reduce((s, r) => (keyOf(r, seriesDimension) === key ? s + (value(metricId, r) ?? 0) : s), 0);
  const chosen = allSeries.length > MAX_SERIES ? [...allSeries].sort((x, y) => total(y.key) - total(x.key)).slice(0, MAX_SERIES) : allSeries;
  const cell = new Map<string, number | null>();
  for (const r of rows) cell.set(`${keyOf(r, categoryDimension)}\u0000${keyOf(r, seriesDimension)}`, value(metricId, r));
  const series = chosen.map((s): SeriesModel => ({ key: s.key, label: s.label, values: categories.map((c) => cell.get(`${c.key}\u0000${s.key}`) ?? null) }));
  return { categories, series, truncated: { categories: allCategories.length - categories.length, series: allSeries.length - chosen.length }, categoryDimension, seriesDimension };
}

export type { BiUnit };
